import { useState } from 'react'

function GlobalPlatformSettings() {
  const [settings, setSettings] = useState({
    platform_name: 'SSA Executive Advisory Platform',
    default_client_cap: 15,
    default_plan_tier: 'design_partner',
    maintenance_mode: false
  })

  const [saved, setSaved] = useState(false)

  const handleSave = () => {
    // In a future iteration, persist to organizations.settings where is_platform_owner = true
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Global configuration for the SSA platform</p>
      </div>

      {/* Platform Configuration */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Platform Configuration</h3>
        <div className="space-y-4 max-w-xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform Name</label>
            <input
              type="text"
              value={settings.platform_name}
              onChange={(e) => setSettings(prev => ({ ...prev, platform_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Client Cap</label>
              <input
                type="number"
                value={settings.default_client_cap}
                onChange={(e) => setSettings(prev => ({ ...prev, default_client_cap: parseInt(e.target.value, 10) || 15 }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                min={1}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Plan Tier</label>
              <select
                value={settings.default_plan_tier}
                onChange={(e) => setSettings(prev => ({ ...prev, default_plan_tier: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="design_partner">Design Partner</option>
                <option value="solo">Solo</option>
                <option value="advisory_firm">Advisory Firm</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.maintenance_mode}
                onChange={(e) => setSettings(prev => ({ ...prev, maintenance_mode: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
            </label>
            <span className="text-sm text-gray-700 dark:text-gray-300">Maintenance Mode</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">(Shows banner to all users)</span>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button onClick={handleSave} className="btn-primary">Save Settings</button>
          {saved && (
            <span className="text-sm text-green-600 dark:text-green-400">Settings saved</span>
          )}
        </div>
      </div>

      {/* API Configuration (read-only) */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">API Configuration</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          API keys are configured via environment variables and cannot be changed here.
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-sm text-gray-700 dark:text-gray-300">Supabase URL</span>
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">Configured</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-sm text-gray-700 dark:text-gray-300">Supabase Anon Key</span>
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">Configured</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-sm text-gray-700 dark:text-gray-300">Anthropic API Key</span>
            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">Configured</span>
          </div>
        </div>
      </div>

      {/* Pricing Reference */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Plan Pricing Reference</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Platform Fee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Per Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Default Cap</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">Design Partner</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">$350/mo</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">$100/mo</td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">15</td>
              </tr>
              <tr>
                <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">Solo</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">$750/mo</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">$150/mo</td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">15</td>
              </tr>
              <tr>
                <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">Advisory Firm</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">$1,500/mo</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">$125/mo</td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">50</td>
              </tr>
              <tr>
                <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">Enterprise</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">Custom</td>
                <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">Custom</td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">Unlimited</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default GlobalPlatformSettings
